import api from './client';

export interface Folder {
  id: number;
  account: number;
  name: string;
  imap_name: string;
  parent: number | null;
  special_use: string;
  delimiter?: string;
  unread_count: number;
  total_count: number;
  last_synced: string | null;
}

export interface MessageSummary {
  id: number;
  uid: number;
  subject: string;
  from_address: string;
  to_addresses: string;
  cc_addresses?: string;
  date: string;
  is_read: boolean;
  is_starred: boolean;
  has_attachments: boolean;
  is_signed: boolean;
  is_encrypted: boolean;
  folder: number;
  thread: number | null;
}

export interface Attachment {
  id: number;
  /** 0-based part index for live IMAP attachments */
  index?: number;
  filename: string;
  content_type: string;
  size: number;
  file: string | null;
  is_pgp_public_key?: boolean;
  /** True when fingerprints in this attachment are already in the keyring. */
  pgp_key_already_imported?: boolean;
}

export interface MessageDetail extends MessageSummary {
  message_id: string;
  in_reply_to: string;
  references: string;
  cc_addresses: string;
  bcc_addresses: string;
  reply_to: string;
  size: number;
  is_draft: boolean;
  is_deleted: boolean;
  body_text: string;
  body_html: string;
  gpg_verified: boolean | null;
  attachments: Attachment[];
  fetched_at?: string | null;
}

export interface ThreadSummary {
  id: number;
  thread_id: string;
  subject: string;
  last_message_date: string;
  has_unread: boolean;
  message_count: number;
  latest_from: string;
  latest_message_id: number | null;
  participants: string[];
  folder_id?: number | null;
  folder_name?: string;
  has_attachments?: boolean;
  size?: number;
}

export interface Thread {
  id: number;
  thread_id: string;
  subject: string;
  last_message_date: string;
  has_unread: boolean;
  message_count: number;
  messages: MessageSummary[];
}

export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

// Folders
export const getFolders = (params?: Record<string, unknown>) =>
  api.get<PaginatedResponse<Folder>>('/mailbox/folders/', { params }).then((r) => r.data);

export const createFolder = (data: { name: string }) =>
  api.post<Folder>('/mailbox/folders/', data).then((r) => r.data);

// Messages (DB-backed; used by search results)
export const getMessage = (id: number) =>
  api.get<MessageDetail>(`/mailbox/messages/${id}/`).then((r) => r.data);

export const getMessageRaw = (id: number) =>
  api.get<{ id: number; raw: string }>(`/mailbox/messages/${id}/raw/`).then((r) => r.data);

export const markStarred = (id: number, starred: boolean) =>
  api.post(`/mailbox/messages/${id}/mark-starred/`, { starred });

export const moveMessage = (id: number, folder_id: number) =>
  api.post(`/mailbox/messages/${id}/move/`, { folder_id }).then((r) => r.data);

export const copyMessage = (id: number, folder_id: number) =>
  api.post(`/mailbox/messages/${id}/copy/`, { folder_id }).then((r) => r.data);

export const trashMessage = (id: number) => api.post(`/mailbox/messages/${id}/trash/`);

export const archiveMessage = (id: number) => api.post(`/mailbox/messages/${id}/archive/`);

// Threads
export const getThreads = (params?: Record<string, unknown>) =>
  api.get<PaginatedResponse<ThreadSummary>>('/mailbox/threads/', { params }).then((r) => r.data);

export const getThread = (id: number) =>
  api.get<Thread>(`/mailbox/threads/${id}/`).then((r) => r.data);

// Compose
export interface ComposePayload {
  account_id?: number;
  alias_id?: number | null;
  from_address?: string;
  to: string[];
  cc?: string[];
  bcc?: string[];
  subject: string;
  body_text?: string;
  body_html?: string;
  reply_to?: string;
  in_reply_to?: string;
  references?: string;
  sign?: boolean;
  encrypt?: boolean;
  attach_public_key?: boolean;
  gpg_passphrase?: string;
}

export const sendMessage = (payload: ComposePayload) =>
  api.post('/mailbox/compose/', payload).then((r) => r.data);

// Filter rules
export interface FilterRule {
  id: number;
  account: number;
  name: string;
  is_active: boolean;
  order: number;
  condition_field: string;
  condition_value: string;
  action: string;
  action_folder: number | null;
}

export const getFilterRules = () =>
  api.get<PaginatedResponse<FilterRule>>('/mailbox/filter-rules/').then((r) => r.data);

export const createFilterRule = (data: Partial<FilterRule>) =>
  api.post<FilterRule>('/mailbox/filter-rules/', data).then((r) => r.data);

export const updateFilterRule = (id: number, data: Partial<FilterRule>) =>
  api.patch<FilterRule>(`/mailbox/filter-rules/${id}/`, data).then((r) => r.data);

export const deleteFilterRule = (id: number) => api.delete(`/mailbox/filter-rules/${id}/`);

export interface GpgKey {
  fingerprint: string;
  uids: string[];
  length?: string;
  algo?: string;
  date?: string;
  expires?: string;
  has_secret: boolean;
}

export const getGpgKeys = () =>
  api.get<{ results: GpgKey[] }>('/mailbox/gpg/keys/').then((r) => r.data);

export const importGpgKey = (key_data: string) =>
  api.post('/mailbox/gpg/keys/', { action: 'import', key_data }).then((r) => r.data);

export const generateGpgKey = (data: { name: string; email: string; passphrase?: string }) =>
  api.post('/mailbox/gpg/keys/', { action: 'generate', ...data }).then((r) => r.data);

export const exportGpgKey = (fingerprint: string, secret = false) =>
  api
    .get<{ fingerprint: string; key_data: string }>(
      `/mailbox/gpg/keys/${encodeURIComponent(fingerprint)}/`,
      {
        params: { secret: secret ? 'true' : 'false' },
      }
    )
    .then((r) => r.data);

export const deleteGpgKey = (fingerprint: string, passphrase?: string) =>
  api.delete(`/mailbox/gpg/keys/${encodeURIComponent(fingerprint)}/`, {
    data: { passphrase: passphrase ?? '' },
  });

export const decryptGpgPayload = (data: string, passphrase?: string) =>
  api
    .post<{
      plaintext: string;
      verified?: boolean;
      fingerprint?: string | null;
      signatures?: Array<{ key_id?: string; status?: string }>;
    }>('/mailbox/gpg/decrypt/', {
      data,
      passphrase: passphrase ?? '',
    })
    .then((r) => r.data);

// ---------------------------------------------------------------------------
// Live IMAP (browse/open without DB sync)
// ---------------------------------------------------------------------------

export interface LiveFolder extends Folder {
  parent_imap_name?: string | null;
}

export interface LiveThreadSummary extends ThreadSummary {
  account: number;
  imap_folder: string;
  latest_uid: number;
  message_uids?: number[];
}

export interface LiveMessageSummary {
  id: number | null;
  uid: number;
  subject: string;
  from_address: string;
  to_addresses: string;
  cc_addresses?: string;
  date: string;
  is_read: boolean;
  is_starred: boolean;
  has_attachments: boolean;
  is_signed: boolean;
  is_encrypted: boolean;
  folder: number;
  thread: number | null;
  account: number;
  imap_folder: string;
}

export interface LiveMessageDetail extends Omit<MessageDetail, 'id' | 'thread'> {
  id: number | null;
  thread: number | null;
  account: number;
  imap_folder: string;
  uid: number;
  raw?: string;
}

export interface LiveThreadDetail {
  id: number | null;
  thread_id: string;
  subject: string;
  last_message_date: string | null;
  has_unread: boolean;
  message_count: number;
  account: number;
  imap_folder: string;
  messages: LiveMessageSummary[];
}

export const getLiveFolders = (params?: { account?: number }) =>
  api
    .get<PaginatedResponse<LiveFolder>>('/mailbox/imap/folders/', { params })
    .then((r) => r.data);

export const getLiveThreads = (params: {
  account: number;
  folder: string;
  page?: number;
  page_size?: number;
  sort_by?: 'date' | 'from' | 'size';
  order?: 'asc' | 'desc';
}) =>
  api
    .get<PaginatedResponse<LiveThreadSummary>>('/mailbox/imap/threads/', { params })
    .then((r) => r.data);

export const getLiveThread = (params: {
  account: number;
  folder: string;
  thread_id: string;
  uids?: string;
}) => api.get<LiveThreadDetail>('/mailbox/imap/thread/', { params }).then((r) => r.data);

export const getLiveMessage = (params: { account: number; folder: string; uid: number }) =>
  api.get<LiveMessageDetail>('/mailbox/imap/message/', { params }).then((r) => r.data);

export const getLiveMessageRaw = (params: { account: number; folder: string; uid: number }) =>
  api.get<{ uid: number; raw: string }>('/mailbox/imap/message/raw/', { params }).then((r) => r.data);

export const downloadLiveAttachment = async (params: {
  account: number;
  folder: string;
  uid: number;
  index: number;
  disposition?: 'attachment' | 'inline';
}) => {
  const r = await api.get('/mailbox/imap/attachment/', {
    params: {
      account: params.account,
      folder: params.folder,
      uid: params.uid,
      index: params.index,
      disposition: params.disposition || 'attachment',
    },
    responseType: 'blob',
  });
  return r.data as Blob;
};

export const downloadDbAttachment = async (params: {
  messageId: number;
  attachmentId: number;
  disposition?: 'attachment' | 'inline';
}) => {
  const r = await api.get(
    `/mailbox/messages/${params.messageId}/attachments/${params.attachmentId}/download/`,
    {
      params: { disposition: params.disposition || 'attachment' },
      responseType: 'blob',
    }
  );
  return r.data as Blob;
};

export const readAttachmentText = async (opts: {
  live?: { account: number; folder: string; uid: number };
  messageId?: number;
  attachment: Attachment;
}) => {
  const blob = opts.live
    ? await downloadLiveAttachment({
        ...opts.live,
        index: opts.attachment.index ?? opts.attachment.id,
        disposition: 'attachment',
      })
    : await downloadDbAttachment({
        messageId: opts.messageId!,
        attachmentId: opts.attachment.id,
        disposition: 'attachment',
      });
  return blob.text();
};

export const liveMarkFlags = (data: {
  account: number;
  folder: string;
  uid: number;
  read?: boolean;
  starred?: boolean;
}) => api.post('/mailbox/imap/flags/', data);

export const liveMoveMessage = (data: {
  account: number;
  folder: string;
  uid?: number;
  uids?: number[];
  dest_folder: string;
  dest_account?: number;
}) => api.post('/mailbox/imap/move/', data);

export const liveCopyMessage = (data: {
  account: number;
  folder: string;
  uid?: number;
  uids?: number[];
  dest_folder: string;
  dest_account?: number;
}) => api.post('/mailbox/imap/copy/', data);

export const liveTrashMessage = (data: {
  account: number;
  folder: string;
  uid?: number;
  uids?: number[];
}) => api.post('/mailbox/imap/trash/', data);

export const liveArchiveMessage = (data: {
  account: number;
  folder: string;
  uid?: number;
  uids?: number[];
}) => api.post('/mailbox/imap/archive/', data);

export const getLiveUids = (params: { account: number; folder: string }) =>
  api
    .get<{ account: number; folder: string; count: number; uids: number[] }>(
      '/mailbox/imap/uids/',
      { params }
    )
    .then((r) => r.data);

export const liveEmptyTrash = (data: { account: number }) =>
  api.post<{ detail: string; count: number }>('/mailbox/imap/empty-trash/', data);
